import { Controller } from "stimulus";
import { FloatingMenu } from "utils/floating_menu";

// The post audience picker (see Block::PostAudienceComponent). Picking an audience fills the two named
// fields in the auto-save group and fires input + change on the first one, which is what makes auto-save
// send the group. The radios and checkboxes themselves are never submitted.
export default class extends Controller {
  static targets = ["trigger", "panel", "label", "mode", "plans", "plan", "audienceField", "planIdsField"];

  connect() {
    this.floatingMenu = new FloatingMenu({ menu: this.panelTarget, anchor: this.triggerTarget });
  }

  disconnect() {
    clearTimeout(this.saveTimer);
    this.floatingMenu?.disconnect();
  }

  toggle() {
    if (this.panelTarget.classList.contains("hidden")) this.open();
    else this.close();
  }

  open() {
    this.panelTarget.classList.remove("hidden");
    this.floatingMenu.open();
    this.triggerTarget.setAttribute("aria-expanded", "true");
  }

  close() {
    this.panelTarget.classList.add("hidden");
    this.floatingMenu.close();
    this.triggerTarget.setAttribute("aria-expanded", "false");
  }

  // The panel stays inside this element in the DOM, so a click in it is not "outside".
  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  select() {
    this.plansTarget.classList.toggle("hidden", this.mode !== "plans");
    this.update();
  }

  togglePlan() {
    this.update();
  }

  get mode() {
    return this.modeTargets.find(radio => radio.checked)?.value || "everyone";
  }

  get planIds() {
    return this.planTargets.filter(box => box.checked).map(box => box.value);
  }

  get planNames() {
    return this.planTargets.filter(box => box.checked).map(box => box.dataset.name);
  }

  get label() {
    switch (this.mode) {
      case "everyone": return "Everyone";
      case "paid": return "All paid plans";
      default: {
        const names = this.planNames;
        if (names.length === 0) return "Choose plans";
        return names.length === 1 ? names[0] : `${names.length} plans`;
      }
    }
  }

  update() {
    const label = this.label;
    this.labelTarget.textContent = label;
    this.triggerTarget.setAttribute("aria-label", `Audience: ${label}`);

    // Specific plans with none ticked yet isn't an audience: nothing is saved until one is.
    if (this.mode === "plans" && this.planIds.length === 0) return;

    const mode = this.mode;
    const planIds = mode === "plans" ? this.planIds.join(",") : this.planIdsFieldTarget.value;
    if (mode === this.audienceFieldTarget.value && planIds === this.planIdsFieldTarget.value) return;

    // The fields hold the choice at once, so a form submit already carries it; the save itself waits a
    // beat so ticking several plans in a row is one request (auto-save drops a change made mid-save).
    this.audienceFieldTarget.value = mode;
    this.planIdsFieldTarget.value = planIds;
    [this.audienceFieldTarget, this.planIdsFieldTarget].forEach(field => { field.name = field.dataset.name; });

    this.dispatch("change", { prefix: "post-audience", detail: { mode, planIds: mode === "plans" ? this.planIds : [], planNames: mode === "plans" ? this.planNames : [], label } });
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.save(), 400);
  }

  save() {
    this.audienceFieldTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.audienceFieldTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
